import * as React from 'react'
import style from './style.module.css'
import animateStyle from './animate.module.css'
import classnames from 'classnames'
import { useEffect, useState, useCallback } from 'react'
import { bridge, defaultConfig } from './bridge'
import { CSSTransition } from 'react-transition-group'

interface IButton {
  text?: string
  className?: string
  disabled?: boolean
  onPress?: (e: React.MouseEvent) => any // 不会自动关闭
  onClick?: (e: React.MouseEvent) => any // 自动关闭
}

export interface IDialogConfig {
  title?: string
  content?: string | React.ReactElement | number
  zIndex?: number
  buttons?: Array<IButton>
  // 是否需要遮罩层背景色
  isHideMask?: boolean
  closeOnClickMask?: Boolean
  boxClass?: string
  type?: 'warning' | 'normal'
  onClose?: () => void, // 关闭后执行
}

export function Dialog() {

  const [config, setConfig] = useState<IDialogConfig>(defaultConfig)
  const [show, setShow] = useState<boolean>(false)
  const [boxShow, setBoxShow] = useState<boolean>(false)

  // 通过一个 bridge 对象在组件初始化的时候，把内部方法暴露到外部
  useEffect(() => {
    bridge.setConfig = (isOpen: boolean, config: IDialogConfig) => {
      if(isOpen) {
        openDialog()
      } else {
        closeDialog()
        return
      }
      setConfig({ ...config })
    }
  }, [])

  // 关闭的时候，如果有onClose事件的话，就调用onClose事件
  useEffect(() => {
    if(!show && config.onClose) {
      setConfig(defaultConfig)
      config.onClose()
    }
  }, [show, config])

  //关闭时添加上关闭的动画
  const closeDialog = () => {
    setBoxShow(false)
    setTimeout(() => {
      setShow(false)
    }, 200)
  }

  const openDialog = useCallback(() => {
    setShow(true)
    setTimeout(() => {
      setBoxShow(true)
    }, 100)
  }, [])

  /**
   * 按钮点击 点击后自动关闭
   *
   * 自动关闭的需求不符合某些场景，如果需要自动关闭用onClick，不需要的话用onPress
  */
  const buttonClick = useCallback(
    (fn: ((e: React.MouseEvent) => any) | undefined,
    e,
    autoClose: boolean
  ) => {
    if(fn) {
      const isOk = fn(e)
      if(isOk === false) return //以防特殊需求，返回false的话就不关闭弹窗了
    }
    if(autoClose) {
      closeDialog()
    }
  }, [])

  //判断是否为warning类型
  const isWarning = config.type && config.type === 'warning' ? 'warning' : 'submit'

  //目前只能配置两个按钮，之后迭代多按钮方案
  const submitButton = config.buttons && config.buttons[0]
  const cancelButton = config.buttons && config.buttons[1]

  const footer = (
    <div className={
      classnames(
        style['dialog-footer'],
        config.buttons && config.buttons.length > 1 ?
          style['dialog-footer-double'] :
          style['dialog-footer-simple']
      )}
    >
      {
        cancelButton &&
        <button
          onClick={
            (e: React.MouseEvent) => {
              const fn = cancelButton.onPress || cancelButton.onClick
              const autoClose = !cancelButton.onPress // onPress存在的话优先用onPress
              buttonClick(fn, e, autoClose)
            }
          }
          disabled={cancelButton.disabled}
          className={
            classnames(
              style['button'],
              style['cancel'],
              cancelButton.disabled && style['cancel-disabled'],
              cancelButton.className
            )
          }
        >
          {cancelButton.text ? cancelButton.text : '取消'}
        </button>
      }
      {
        submitButton &&
        <button
          onClick={(e: React.MouseEvent) => {
            const fn = submitButton.onPress || submitButton.onClick
            const autoClose = !submitButton.onPress // onPress存在的话优先用onPress
            buttonClick(fn, e, autoClose)
          }}
          disabled={submitButton.disabled}
          className={
            classnames(
              style['button'],
              style[isWarning],
              submitButton.disabled && config.type !== 'warning' && style['submit-disabled'],
              submitButton.className
            )
          }
        >
          {submitButton.text ? submitButton.text: '确认'}
        </button>
      }
    </div>
  )

  const body = (
    <div className={style["dialog-main"]}>
      {/* 内容 */}
      { config.content }
    </div>
  )

  return (
    <CSSTransition
      in={show}
      timeout={200}
      classNames = {{
          enter: animateStyle['dialog-enter'],
          enterActive: animateStyle['dialog-active-enter'],
          exit: animateStyle['dialog-exit'],
          exitActive: animateStyle['dialog-active-exit'],
         }}
      unmountOnExit
    >
      <div
        className={style['dialog-box']}
        id='weike-ui-dialog'
        style={{ zIndex: config.zIndex ?  config.zIndex : 100 }}
      >
        {/* 遮罩层 */}
        <div
          className={classnames(config.isHideMask ? style['mask-hide'] : style['mask'])}
        />
        <div
          className={style['dialog-position']}
          onClick={() => config.closeOnClickMask ? closeDialog() : false}
        >
          <CSSTransition
            in={boxShow}
            timeout={100}
            classNames = {{
                enter: animateStyle['box-enter'],
                enterActive: animateStyle['box-active-enter'],
                exit: animateStyle['box-exit'],
                exitActive: animateStyle['box-active-exit'],
               }}
            unmountOnExit
          >
            <div
              className={
                classnames(
                  style['dialog-main-box'],
                  config.boxClass
                )
              }
              onClick={e => e.stopPropagation()}
            >
              {/* 标题*/}
              { config.title && <div className={style['title']}>{config.title}</div> }
              { body }
              { footer }
            </div>
          </CSSTransition>
        </div>
      </div>
    </CSSTransition>
  )
}



// WEBPACK FOOTER //
// ../src/popup-layer/dialog/index.tsx